<template>
  <HoppSmartModal
    v-if="show"
    dialog
    :title="t('team.invite')"
    @close="hideModal"
  >
    <template #body>
      <div v-if="sendInvitesResult.length" class="flex flex-col px-4">
        <div class="mb-8 flex max-w-md flex-col items-center justify-center">
          <icon-lucide-users class="h-6 w-6 text-accent" />
          <h3 class="my-2 text-center text-lg">
            {{
              isGroupAssignment
                ? t("additional.group_assigned_successfully_title")
                : inviteMethod === "email"
                  ? t("team.we_sent_invite_link")
                  : t("team.invite_sent_smtp_disabled")
            }}
          </h3>
          <p class="text-center">
            {{
              isGroupAssignment
                ? t("additional.group_assigned_successfully_description")
                : inviteMethod === "email"
                  ? t("team.we_sent_invite_link_description")
                  : t("team.invite_sent_smtp_disabled_description")
            }}
          </p>
        </div>

        <div v-if="successInvites.length">
          <label class="mb-4 block">
            {{
              isGroupAssignment
                ? t("additional.success_group_assignments")
                : t("team.success_invites")
            }}
          </label>
          <div
            class="flex flex-col space-y-6 rounded border border-dividerLight p-4"
          >
            <div
              v-for="(invitee, index) in successInvites"
              :key="`invitee-${index}`"
              class="flex items-center"
            >
              <p class="flex items-center flex-1">
                <component
                  :is="isGroupAssignment ? IconUserCheck : IconMailCheck"
                  class="svg-icons mr-4 text-green-500"
                />
                <span class="truncate">{{ invitee.handle }}</span>
                <span class="ml-2 text-secondaryLight">
                  ({{ invitee.role }})
                </span>
                <span
                  v-if="!isGroupAssignment"
                  class="flex items-center gap-1 ml-auto"
                >
                  <HoppButtonSecondary
                    outline
                    filled
                    :icon="getCopyIcon(invitee.invitationID).value"
                    class="rounded-md"
                    :label="t('team.copy_invite_link')"
                    @click="
                      () => {
                        copyInviteLink(invitee.invitationID)
                      }
                    "
                  />
                </span>
              </p>
            </div>
          </div>
        </div>
        <div v-if="failedInvites.length" class="mt-6">
          <label class="mb-4 block">
            {{ t("team.failed_invites") }}
          </label>
          <div
            class="flex flex-col space-y-6 rounded border border-dividerLight p-4"
          >
            <div
              v-for="(invitee, index) in failedInvites"
              :key="`invitee-${index}`"
              class="flex flex-col"
            >
              <p class="flex items-center">
                <component
                  :is="IconAlertTriangle"
                  class="svg-icons mr-4 text-red-500"
                />
                <span class="truncate">{{ invitee.handle }}</span>
              </p>
              <p class="ml-8 mt-1 text-secondaryLight text-tiny">
                {{ getErrorMessage(invitee.error) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="sendingInvites"
        class="flex items-center justify-center p-4"
      >
        <HoppSmartSpinner />
      </div>
      <div v-else class="flex flex-col">
        <div class="flex flex-1 items-center justify-between">
          <label for="memberList" class="px-4 pb-4">
            {{ t("team.pending_invites") }}
          </label>
        </div>
        <div class="divide-y divide-dividerLight rounded border border-divider">
          <div
            v-if="pendingInvites.loading"
            class="flex items-center justify-center p-4"
          >
            <HoppSmartSpinner />
          </div>
          <div v-else>
            <div
              v-if="!pendingInvites.loading && E.isRight(pendingInvites.data)"
              class="divide-y divide-dividerLight"
            >
              <div
                v-for="(invitee, index) in pendingInvites.data.right.team
                  ?.teamInvitations"
                :key="`invitee-${index}`"
                class="flex divide-x divide-dividerLight"
              >
                <input
                  v-if="invitee"
                  class="flex flex-1 bg-transparent px-4 py-2 text-secondaryLight"
                  :placeholder="`${t('team.email')}`"
                  :name="'param' + index"
                  :value="invitee.inviteeEmail"
                  readonly
                />
                <input
                  class="flex flex-1 bg-transparent px-4 py-2 text-secondaryLight"
                  :placeholder="`${t('team.permissions')}`"
                  :name="'value' + index"
                  :value="invitee.inviteeRole"
                  readonly
                />
                <div class="flex">
                  <HoppButtonSecondary
                    v-tippy="{ theme: 'tooltip' }"
                    outline
                    :icon="getCopyIcon(invitee.id).value"
                    class="rounded-md"
                    :title="t('team.copy_invite_link')"
                    @click="
                      () => {
                        copyInviteLink(invitee.id)
                      }
                    "
                  />
                </div>
                <div class="flex">
                  <HoppButtonSecondary
                    v-tippy="{ theme: 'tooltip' }"
                    :title="t('action.remove')"
                    :icon="IconTrash"
                    color="red"
                    :loading="isLoadingIndex === index"
                    @click="removeInvitee(invitee.id, index)"
                  />
                </div>
              </div>
            </div>
            <HoppSmartPlaceholder
              v-if="
                E.isRight(pendingInvites.data) &&
                pendingInvites.data.right.team?.teamInvitations.length === 0
              "
              :src="`/images/states/${colorMode.value}/add_group.svg`"
              :alt="t('empty.pending_invites')"
              :text="t('empty.pending_invites')"
            />
            <div
              v-if="!pendingInvites.loading && E.isLeft(pendingInvites.data)"
              class="flex flex-col items-center p-4"
            >
              <icon-lucide-help-circle class="svg-icons mb-4" />
              {{ t("error.something_went_wrong") }}
            </div>
          </div>
        </div>

        <div class="mt-4 rounded-md">
          <HoppSmartTabs v-model="activeTab">
            <HoppSmartTab id="users" :label="t('users.users')"></HoppSmartTab>
            <HoppSmartTab
              id="groups"
              :label="t('additional.groups')"
            ></HoppSmartTab>
          </HoppSmartTabs>
        </div>

        <template v-if="activeTab === 'users'">
          <div class="flex flex-1 items-center justify-between pt-4">
            <label for="memberList" class="p-4">
              {{ t("team.invite_tooltip") }}
            </label>
            <div class="flex">
              <HoppButtonSecondary
                :icon="IconPlus"
                :label="t('add.new')"
                filled
                @click="addNewInvitee"
              />
            </div>
          </div>
          <div
            class="divide-y divide-dividerLight rounded border border-divider"
          >
            <div
              v-for="(invitee, index) in newInvites"
              :key="`new-invitee-${index}`"
              class="flex divide-x divide-dividerLight"
            >
              <input
                v-model="invitee.key"
                class="flex flex-1 bg-transparent px-4 py-2"
                :placeholder="`${t('team.email')}`"
                :name="'invitee' + index"
                autofocus
              />
              <span>
                <tippy
                  interactive
                  trigger="click"
                  theme="popover"
                  :on-shown="() => tippyActions![index].focus()"
                >
                  <HoppSmartSelectWrapper>
                    <input
                      class="flex flex-1 cursor-pointer bg-transparent px-4 py-2"
                      :placeholder="`${t('team.permissions')}`"
                      :name="'value' + index"
                      :value="invitee.value"
                      readonly
                    />
                  </HoppSmartSelectWrapper>
                  <template #content="{ hide }">
                    <div
                      ref="tippyActions"
                      class="flex flex-col focus:outline-none"
                      tabindex="0"
                      @keyup.escape="hide()"
                    >
                      <HoppSmartItem
                        label="OWNER"
                        :icon="
                          invitee.value === 'OWNER' ? IconCircleDot : IconCircle
                        "
                        :active="invitee.value === 'OWNER'"
                        @click="
                          () => {
                            updateNewInviteeRole(index, TeamAccessRole.Owner)
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="EDITOR"
                        :icon="
                          invitee.value === 'EDITOR'
                            ? IconCircleDot
                            : IconCircle
                        "
                        :active="invitee.value === 'EDITOR'"
                        @click="
                          () => {
                            updateNewInviteeRole(index, TeamAccessRole.Editor)
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="VIEWER"
                        :icon="
                          invitee.value === 'VIEWER'
                            ? IconCircleDot
                            : IconCircle
                        "
                        :active="invitee.value === 'VIEWER'"
                        @click="
                          () => {
                            updateNewInviteeRole(index, TeamAccessRole.Viewer)
                            hide()
                          }
                        "
                      />
                    </div>
                  </template>
                </tippy>
              </span>
              <div class="flex">
                <HoppButtonSecondary
                  id="member"
                  v-tippy="{ theme: 'tooltip' }"
                  :title="t('action.remove')"
                  :icon="IconTrash"
                  color="red"
                  @click="removeNewInvitee(index)"
                />
              </div>
            </div>
            <HoppSmartPlaceholder
              v-if="newInvites.length === 0"
              :src="`/images/states/${colorMode.value}/add_group.svg`"
              :alt="`${t('empty.invites')}`"
              :text="`${t('empty.invites')}`"
            >
              <template #body>
                <HoppButtonSecondary
                  :label="t('add.new')"
                  filled
                  @click="addNewInvitee"
                />
              </template>
            </HoppSmartPlaceholder>
          </div>
        </template>
        <template v-if="activeTab === 'groups'">
          <div class="flex flex-1 items-center justify-between pt-4">
            <label for="groupList" class="p-4">
              {{ t("additional.invite_group_tooltip") }}
            </label>
            <div class="flex">
              <HoppButtonSecondary
                :icon="IconPlus"
                :label="t('add.new')"
                filled
                @click="addNewGroupInvitee"
              />
            </div>
          </div>
          <div
            class="divide-y divide-dividerLight rounded border border-divider"
          >
            <div
              v-for="(invitee, index) in newGroupInvites"
              :key="`new-group-invitee-${index}`"
              class="flex divide-x divide-dividerLight"
            >
              <div class="relative flex flex-1">
                <input
                  v-if="!invitee.selectedGroup"
                  v-model="invitee.searchTerm"
                  class="flex flex-1 bg-transparent px-4 py-2"
                  :placeholder="t('additional.select_group')"
                  :name="'group-invitee' + index"
                  autocomplete="off"
                  @focus="handleInputFocus(index)"
                  @blur="handleInputBlur(index)"
                />
                <div
                  v-if="
                    !invitee.selectedGroup &&
                    !loadingGroups &&
                    userGroups[index]?.length > 0 &&
                    focusedInputIndex === index
                  "
                  class="absolute top-full left-0 z-20 w-full bg-primary border border-dividerLight rounded-lg mt-2 max-h-64 overflow-y-auto shadow-lg transition-all duration-200 ease-in-out"
                  style="overflow-anchor: none"
                >
                  <div
                    v-for="group in userGroups[index]"
                    :key="group.id"
                    class="px-4 py-3 hover:bg-primaryLight cursor-pointer border-b border-dividerLight last:border-b-0 transition-colors duration-150 ease-in-out flex-shrink-0"
                    @click="selectGroup(index, group)"
                  >
                    <div class="font-medium text-primary mb-1">
                      {{ group.name }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="loadingGroups"
                  class="absolute top-full left-0 z-20 w-full bg-primary border border-dividerLight rounded-lg mt-2 py-6 text-center shadow-lg"
                >
                  <HoppSmartSpinner />
                </div>
                <div
                  v-if="invitee.selectedGroup"
                  class="flex flex-1 items-center bg-transparent px-4 py-2 cursor-pointer hover:bg-primaryLight"
                  @click="clearSelectedGroup(index)"
                >
                  <div class="flex-1">
                    <div class="font-medium">
                      {{ invitee.selectedGroup.name }}
                    </div>
                  </div>
                  <div class="ml-2">
                    <icon-lucide-x
                      class="opacity-50 hover:opacity-100"
                      size="16"
                    />
                  </div>
                </div>
              </div>
              <span>
                <tippy
                  interactive
                  trigger="click"
                  theme="popover"
                  :on-shown="() => tippyGroupActions![index]?.focus()"
                >
                  <HoppSmartSelectWrapper>
                    <input
                      class="flex flex-1 cursor-pointer bg-transparent px-4 py-2"
                      :placeholder="`${t('team.permissions')}`"
                      :name="'group-value' + index"
                      :value="invitee.role"
                      readonly
                    />
                  </HoppSmartSelectWrapper>
                  <template #content="{ hide }">
                    <div
                      ref="tippyGroupActions"
                      class="flex flex-col focus:outline-none"
                      tabindex="0"
                      @keyup.escape="hide()"
                    >
                      <HoppSmartItem
                        label="OWNER"
                        :icon="
                          invitee.role === 'OWNER' ? IconCircleDot : IconCircle
                        "
                        :active="invitee.role === 'OWNER'"
                        @click="
                          () => {
                            updateNewGroupInviteeRole(
                              index,
                              TeamAccessRole.Owner
                            )
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="EDITOR"
                        :icon="
                          invitee.role === 'EDITOR' ? IconCircleDot : IconCircle
                        "
                        :active="invitee.role === 'EDITOR'"
                        @click="
                          () => {
                            updateNewGroupInviteeRole(
                              index,
                              TeamAccessRole.Editor
                            )
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="VIEWER"
                        :icon="
                          invitee.role === 'VIEWER' ? IconCircleDot : IconCircle
                        "
                        :active="invitee.role === 'VIEWER'"
                        @click="
                          () => {
                            updateNewGroupInviteeRole(
                              index,
                              TeamAccessRole.Viewer
                            )
                            hide()
                          }
                        "
                      />
                    </div>
                  </template>
                </tippy>
              </span>
              <div class="flex">
                <HoppButtonSecondary
                  v-tippy="{ theme: 'tooltip' }"
                  :title="t('action.remove')"
                  :icon="IconTrash"
                  color="red"
                  @click="removeNewGroupInvitee(index)"
                />
              </div>
            </div>
            <HoppSmartPlaceholder
              v-if="newGroupInvites.length === 0"
              :src="`/images/states/${colorMode.value}/add_group.svg`"
              :alt="`${t('additional.group_invites')}`"
              :text="`${t('additional.group_invites')}`"
            >
              <template #body>
                <HoppButtonSecondary
                  :label="t('add.new')"
                  filled
                  @click="addNewGroupInvitee"
                />
              </template>
            </HoppSmartPlaceholder>
          </div>
        </template>
        <div
          v-if="
            (activeTab === 'users' && newInvites.length) ||
            (activeTab === 'groups' && newGroupInvites.length)
          "
          class="mt-4 flex flex-col items-start rounded border border-dividerLight px-4 py-4"
        >
          <span
            class="mb-4 flex items-center justify-center rounded-full border border-divider bg-primaryDark px-2 py-1 font-semibold"
          >
            <icon-lucide-help-circle
              class="svg-icons mr-2 text-secondaryLight"
            />
            {{ t("profile.roles") }}
          </span>
          <p>
            <span class="text-secondaryLight">
              {{ t("profile.roles_description") }}
            </span>
          </p>
          <ul class="mt-4 space-y-4">
            <li class="flex">
              <span
                class="max-w-[4rem] w-1/4 truncate font-semibold uppercase text-secondaryDark"
              >
                {{ t("profile.owner") }}
              </span>
              <span class="flex flex-1">
                {{ t("profile.owner_description") }}
              </span>
            </li>
            <li class="flex">
              <span
                class="max-w-[4rem] w-1/4 truncate font-semibold uppercase text-secondaryDark"
              >
                {{ t("profile.editor") }}
              </span>
              <span class="flex flex-1">
                {{ t("profile.editor_description") }}
              </span>
            </li>
            <li class="flex">
              <span
                class="max-w-[4rem] w-1/4 truncate font-semibold uppercase text-secondaryDark"
              >
                {{ t("profile.viewer") }}
              </span>
              <span class="flex flex-1">
                {{ t("profile.viewer_description") }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <p
        v-if="sendInvitesResult.length"
        class="flex flex-1 justify-between text-secondaryLight"
      >
        <HoppButtonSecondary
          class="link !p-0"
          :label="t('team.invite_more')"
          :icon="IconArrowLeft"
          @click="
            () => {
              sendInvitesResult = []
              newInvites = [
                {
                  key: '',
                  value: TeamAccessRole.Viewer,
                },
              ]
              newGroupInvites = [
                {
                  searchTerm: '',
                  selectedGroup: null,
                  role: TeamAccessRole.Viewer,
                },
              ]
            }
          "
        />
        <HoppButtonSecondary
          class="link !p-0"
          :label="`${t('action.dismiss')}`"
          @click="hideModal"
        />
      </p>
      <span v-else class="flex space-x-2">
        <HoppButtonPrimary
          :label="activeTab === 'users' ? t('team.invite') : t('action.assign')"
          outline
          @click="activeTab === 'users' ? sendInvites() : sendGroupInvites()"
        />
        <HoppButtonSecondary
          :label="t('action.cancel')"
          outline
          filled
          @click="hideModal"
        />
      </span>
    </template>
  </HoppSmartModal>
</template>

<script setup lang="ts">
import { useGQLQuery } from "@hoppscotch/common/composables/graphql"
import * as A from "fp-ts/Array"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as T from "fp-ts/Task"
import { flow, pipe } from "fp-ts/function"
import { computed, onMounted, reactive, ref, Ref, watch } from "vue"
import {
  GQLError,
  runGQLQuery,
  runMutation,
} from "@hoppscotch/common/helpers/backend/GQLClient"
import {
  GetPendingInvitesDocument,
  GetPendingInvitesQuery,
  GetPendingInvitesQueryVariables,
  TeamInvitationAddedDocument,
  TeamInvitationRemovedDocument,
  TeamAccessRole,
} from "@hoppscotch/common/helpers/backend/graphql"
import {
  createTeamInvitation,
  CreateTeamInvitationErrors,
  revokeTeamInvitation,
} from "@hoppscotch/common/helpers/backend/mutations/TeamInvitation"
import {
  Email,
  EmailCodec,
} from "@hoppscotch/common/helpers/backend/types/Email"

import { useI18n } from "@hoppscotch/common/composables/i18n"
import { useToast } from "@hoppscotch/common/composables/toast"
import { useColorMode } from "@hoppscotch/common/composables/theming"

import { refAutoReset } from "@vueuse/core"
import { TippyComponent } from "vue-tippy"
import { copyToClipboard } from "@hoppscotch/common/helpers/utils/clipboard"
import { platform } from "@hoppscotch/common/platform"

import {
  AddUserGroupToTeamDocument,
  GetUserGroupsDocument,
  GetUserGroupsQuery,
  GetUserGroupsQueryVariables,
} from "@app/api/generated/graphql"

import IconAlertTriangle from "~icons/lucide/alert-triangle"
import IconArrowLeft from "~icons/lucide/arrow-left"
import IconCheck from "~icons/lucide/check"
import IconCircle from "~icons/lucide/circle"
import IconCircleDot from "~icons/lucide/circle-dot"
import IconCopy from "~icons/lucide/copy"
import IconMailCheck from "~icons/lucide/mail-check"
import IconUserCheck from "~icons/lucide/user-check"
import IconPlus from "~icons/lucide/plus"
import IconTrash from "~icons/lucide/trash"

interface UserGroup {
  id: string
  name: string
  description?: string
  userCount?: number
}

const copyIcons: Record<string, Ref<typeof IconCopy | typeof IconCheck>> = {}
const getCopyIcon = (id: string) => {
  if (!copyIcons[id]) {
    copyIcons[id] = refAutoReset<typeof IconCopy | typeof IconCheck>(
      IconCopy,
      1000
    )
  }

  return copyIcons[id]
}

const t = useI18n()

const toast = useToast()

const colorMode = useColorMode()

const activeTab = ref<"users" | "groups">("users")

const tippyActions = ref<TippyComponent[] | null>(null)
const tippyGroupActions = ref<TippyComponent[] | null>(null)

const props = defineProps({
  show: Boolean,
  editingTeamID: { type: String, default: null },
})

const emit = defineEmits<{
  (e: "hide-modal"): void
}>()

const inviteMethod = ref<"email" | "link">("email")

let organizationDomain = ""

onMounted(async () => {
  const getIsSMTPEnabled = platform.infra?.getIsSMTPEnabled

  if (getIsSMTPEnabled) {
    const res = await getIsSMTPEnabled()

    if (E.isRight(res)) {
      inviteMethod.value = res.right ? "email" : "link"
    }
  }

  const { organization } = platform

  if (!organization) {
    return
  }

  if (!organization.isDefaultCloudInstance) {
    const orgInfo = await organization.getOrgInfo()

    organizationDomain = orgInfo?.orgDomain ?? ""
  }
})

const pendingInvites = useGQLQuery<
  GetPendingInvitesQuery,
  GetPendingInvitesQueryVariables,
  ""
>({
  query: GetPendingInvitesDocument,
  variables: reactive({
    teamID: props.editingTeamID,
  }),
  pollDuration: 10000,
  pollLoadingEnabled: false,
  updateSubs: computed(() =>
    !props.editingTeamID
      ? []
      : [
          {
            key: 4,
            query: TeamInvitationAddedDocument,
            variables: {
              teamID: props.editingTeamID,
            },
          },
          {
            key: 5,
            query: TeamInvitationRemovedDocument,
            variables: {
              teamID: props.editingTeamID,
            },
          },
        ]
  ),
  defer: true,
})

watch(
  () => props.show,
  (show) => {
    if (!show) {
      pendingInvites.pause()
    } else {
      pendingInvites.unpause()
    }
  }
)

watch(
  () => props.editingTeamID,
  () => {
    if (props.editingTeamID) {
      pendingInvites.execute({
        teamID: props.editingTeamID,
      })
    }
  }
)

const isLoadingIndex = ref<null | number>(null)

const removeInvitee = async (id: string, index: number) => {
  isLoadingIndex.value = index
  const result = await revokeTeamInvitation(id)()
  if (E.isLeft(result)) {
    toast.error(`${t("error.something_went_wrong")}`)
  } else {
    toast.success(`${t("team.member_removed")}`)
  }
  isLoadingIndex.value = null
}

const newInvites = ref<Array<{ key: string; value: TeamAccessRole }>>([
  {
    key: "",
    value: TeamAccessRole.Viewer,
  },
])

const addNewInvitee = () => {
  newInvites.value.push({
    key: "",
    value: TeamAccessRole.Viewer,
  })
}

const updateNewInviteeRole = (index: number, role: TeamAccessRole) => {
  newInvites.value[index].value = role
}

const removeNewInvitee = (id: number) => {
  newInvites.value.splice(id, 1)
}

const newGroupInvites = ref<
  Array<{
    searchTerm: string
    selectedGroup: UserGroup | null
    role: TeamAccessRole
  }>
>([
  {
    searchTerm: "",
    selectedGroup: null,
    role: TeamAccessRole.Viewer,
  },
])

const loadingGroups = ref(false)
const allUserGroups = ref<UserGroup[]>([]) // Store all groups
const userGroups = ref<Array<UserGroup[]>>([])
const groupsLoaded = ref(false)
const focusedInputIndex = ref<number | null>(null)

// Function to fetch all user groups - called once
const fetchUserGroups = async (): Promise<void> => {
  if (groupsLoaded.value) return // If already loaded, don't fetch again

  loadingGroups.value = true
  try {
    const result = await runGQLQuery<
      GetUserGroupsQuery,
      GetUserGroupsQueryVariables,
      ""
    >({
      query: GetUserGroupsDocument,
      variables: {
        take: 50, // Fetch a reasonably large number of groups
        skip: 0,
      },
    })

    if (E.isLeft(result)) {
      toast.error(t("additional.failed_to_search_groups"))
      return
    }

    // Store all groups
    allUserGroups.value =
      result.right.getUserGroups.map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description ?? undefined,
        userCount: group.userCount,
      })) || []

    groupsLoaded.value = true
  } catch (_error) {
    toast.error(t("additional.failed_to_search_groups"))
  } finally {
    loadingGroups.value = false
  }
}

// Function to get the available groups for a specific row (filtering out already selected groups)
const getAvailableGroupsForRow = (index: number): UserGroup[] => {
  // Get all selected group IDs except the current row's selection
  const selectedGroupIds = newGroupInvites.value
    .map((invite, i) =>
      i !== index && invite.selectedGroup ? invite.selectedGroup.id : null
    )
    .filter((id) => id !== null)

  // Return groups that aren't selected in other rows
  return allUserGroups.value.filter(
    (group) => !selectedGroupIds.includes(group.id)
  )
}

// Show all available groups when input is focused
const handleInputFocus = async (index: number) => {
  focusedInputIndex.value = index

  if (!groupsLoaded.value) {
    await fetchUserGroups()
  }

  // Update available groups for this row
  userGroups.value[index] = getAvailableGroupsForRow(index)
}

const handleInputBlur = () => {
  setTimeout(() => {
    focusedInputIndex.value = null
  }, 150)
}

const selectGroup = (index: number, group: UserGroup) => {
  newGroupInvites.value[index].selectedGroup = group
  newGroupInvites.value[index].searchTerm = ""
}

const clearSelectedGroup = (index: number) => {
  newGroupInvites.value[index].selectedGroup = null
  // Update available groups for this row
  userGroups.value[index] = getAvailableGroupsForRow(index)

  // Optional: Focus the input after clearing
  setTimeout(() => {
    const input = document.querySelector(
      `[name="group-invitee${index}"]`
    ) as HTMLInputElement
    if (input) {
      input.focus()
    }
  }, 0)
}

const addNewGroupInvitee = () => {
  newGroupInvites.value.push({
    searchTerm: "",
    selectedGroup: null,
    role: TeamAccessRole.Viewer,
  })

  // If we've already loaded the groups, initialize the available groups for this new row
  if (groupsLoaded.value) {
    const newIndex = newGroupInvites.value.length - 1
    userGroups.value[newIndex] = getAvailableGroupsForRow(newIndex)
  } else {
    userGroups.value.push([])
  }
}

const updateNewGroupInviteeRole = (index: number, role: TeamAccessRole) => {
  newGroupInvites.value[index].role = role
}

const removeNewGroupInvitee = (id: number) => {
  newGroupInvites.value.splice(id, 1)
  userGroups.value.splice(id, 1)

  // Update available groups for remaining rows since removing a selection makes that group available again
  if (groupsLoaded.value) {
    newGroupInvites.value.forEach((_, index) => {
      userGroups.value[index] = getAvailableGroupsForRow(index)
    })
  }
}

const copyInviteLink = (invitationID: string) => {
  let inviteLink = ""

  const { organization } = platform

  if (organization && !organization.isDefaultCloudInstance) {
    const rootDomain = organization?.getRootDomain()
    inviteLink = `https://${organizationDomain}.${rootDomain}/join-team?id=${invitationID}`
  } else {
    inviteLink = `${Object.create(globalThis.import_meta_env || null).VITE_BASE_URL}/join-team?id=${invitationID}`
  }

  copyToClipboard(inviteLink)

  getCopyIcon(invitationID).value = IconCheck
}

type SendInvitesErrorType =
  | {
      handle: string // can be email or group name
      status: "error"
      error: GQLError<CreateTeamInvitationErrors>
    }
  | {
      handle: string // can be email or group name
      status: "success"
      invitationID: string
      role?: TeamAccessRole
    }

const sendInvitesResult = ref<Array<SendInvitesErrorType>>([])
const successInvites = computed(() =>
  sendInvitesResult.value.filter((invitee) => invitee.status === "success")
)
const failedInvites = computed(() =>
  sendInvitesResult.value.filter((invitee) => invitee.status === "error")
)

const sendingInvites = ref<boolean>(false)

const sendInvites = async () => {
  const validationResult = pipe(
    newInvites.value,
    O.fromPredicate(
      (invites): invites is Array<{ key: Email; value: TeamAccessRole }> =>
        pipe(
          invites,
          A.every((invitee) => EmailCodec.is(invitee.key))
        )
    ),
    O.map(
      A.map((invitee) =>
        createTeamInvitation(invitee.key, invitee.value, props.editingTeamID)
      )
    )
  )

  if (O.isNone(validationResult)) {
    toast.error(`${t("error.incorrect_email")}`)
    return
  }

  sendingInvites.value = true

  sendInvitesResult.value = await pipe(
    A.sequence(T.task)(validationResult.value),
    T.chain(
      flow(
        A.mapWithIndex((i, el) =>
          pipe(
            el,
            E.foldW(
              (err) => ({
                status: "error" as const,
                handle: newInvites.value[i].key,
                error: err,
              }),
              (invitation) => ({
                status: "success" as const,
                handle: newInvites.value[i].key,
                invitationID: invitation.id,
                role: newInvites.value[i].value, // Add permission info
              })
            )
          )
        ),
        T.of
      )
    )
  )()

  sendingInvites.value = false
}

const sendGroupInvites = async () => {
  const validGroups = newGroupInvites.value.filter(
    (invite) => invite.selectedGroup !== null
  )

  if (validGroups.length === 0) {
    toast.error(t("additional.no_groups_selected"))
    return
  }

  sendingInvites.value = true

  try {
    const results: Array<SendInvitesErrorType> = []

    for (const groupInvite of validGroups) {
      if (groupInvite.selectedGroup) {
        // Use runMutation to add the user group to the team
        const result = await runMutation(AddUserGroupToTeamDocument, {
          teamID: props.editingTeamID,
          role: groupInvite.role,
          userGroupID: groupInvite.selectedGroup.id,
        })()

        if (E.isRight(result)) {
          // Success case
          results.push({
            status: "success",
            handle: `${groupInvite.selectedGroup.name}`,
            invitationID: `group-invite-${Date.now()}`,
            role: groupInvite.role, // Add role info
          })
        } else {
          // Error case
          results.push({
            status: "error",
            handle: `${groupInvite.selectedGroup.name}`,
            error: result.left,
          })
        }
      }
    }

    sendInvitesResult.value = results

    // Check if there were any successful invites
    if (results.some((result) => result.status === "success")) {
      toast.success(t("additional.group_invited_successfully"))
    }

    // Check if there were any failed invites
    if (results.some((result) => result.status === "error")) {
      toast.error(t("additional.failed_to_invite_some_groups"))
    }
  } catch (_error) {
    toast.error(t("additional.failed_to_invite_group"))
  } finally {
    sendingInvites.value = false
  }
}

const getErrorMessage = (error: SendInvitesErrorType["error"]) => {
  if (error.type === "network_error") {
    return t("error.network_error")
  }
  switch (error.error) {
    case "team/invalid_id":
      return t("team.invalid_id")
    case "team/member_not_found":
      return t("team.member_not_found")
    case "team_invite/already_member":
      return t("team.already_member")
    case "team_invite/member_has_invite":
      return t("team.member_has_invite")
    case "user/not_found":
      return t("team.user_not_found")
  }
}

const hideModal = () => {
  sendingInvites.value = false
  sendInvitesResult.value = []
  activeTab.value = "users"
  groupsLoaded.value = false
  allUserGroups.value = []
  focusedInputIndex.value = null

  // Reset invites
  newInvites.value = [
    {
      key: "",
      value: TeamAccessRole.Viewer,
    },
  ]

  newGroupInvites.value = [
    {
      searchTerm: "",
      selectedGroup: null,
      role: TeamAccessRole.Viewer,
    },
  ]

  userGroups.value = [[]]

  emit("hide-modal")
}

const isGroupAssignment = computed(() => activeTab.value === "groups")
</script>
